import axios from "axios";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ReactPaginate from "react-paginate";
import toast, { Toaster } from "react-hot-toast";
import * as XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import { openAdminChat } from "../../../utils/openAdminChat";

// Every order_status value the filter dropdown can pick from (matches
// orders.js's ENUM / orderController.js). Labels are a plain title-case of
// the value itself (order_tracking -> "Order Tracking") — not an
// editorialized rename — so the dropdown option always reads as the exact
// same status the card badge elsewhere on this page already shows raw
// (that badge is intentionally left as-is, not touched here).
const ORDER_STATUS_VALUES = [
  "pending",
  "order_placed",
  "sales_executive_assigned",
  "graphic_designer_assigned",
  "design_received",
  "quotation_received",
  "order_tracking",
  "order_completed",
  "order_cancelled",
];
const titleCaseStatus = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const RequestForQuotation = () => {
  const Navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState([]);
  const [totalpage, setTotalPage] = useState(0);
  const [page, setPage] = useState(1);
  const [monthFilter, setMonthFilter] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortNewest, setSortNewest] = useState(true);

  // Fixed, known set — was previously derived from whatever order_status
  // values happened to appear on the current (5-row) page, so once data
  // grew, statuses that weren't on page 1 could never be selected as a
  // filter at all.
  const STATUS_OPTIONS = ORDER_STATUS_VALUES.map((value) => ({
    value,
    label: titleCaseStatus(value),
  }));

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (location.state !== null) {
      toast.success(location.state);
      window.history.replaceState({}, "");
    }
  }, []);

  // ✅ Fetch & group by order_id
  const RequestForQuotationData = () => {
    // search/status/month used to only filter whatever 5 rows the current
    // page happened to contain, client-side — meaningless once there are
    // more than a page of RFQs. All three are now sent to the backend
    // (which already supports them, see orderController.getOrdersPagination)
    // so filtering applies across the whole dataset, not just page 1.
    const params = new URLSearchParams();
    params.append("page", page);
    params.append("limit", 10);
    if (query.trim()) params.append("search", query.trim());
    if (statusFilter) params.append("status", statusFilter);
    if (monthFilter) params.append("month", monthFilter);

    axios
      .get(
        `${process.env.REACT_APP_URL}/admin/orders/pagination?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("Token")}` },
        }
      )
      .then((res) => {
        const orders = res?.data?.data?.orders || [];

        const grouped = orders.map((order) => ({
          order_id: order.order_id,
          product_title: order.product?.title,
          final_price: order.final_price,
          final_description: order.final_description,
          product_description: order.product_description,
          order_status: order.order_status,
          size: order.size,
          quantity: order.quantity,
          product_id: order.product_id,
          material_color: order.material_color,
          created_at: order.created_at,
          updated_at: order.updated_at,

          // ✅ include buyer info
          user: order.user
            ? {
                name: order.user.name || "",
                email: order.user.email || "",
                phone: order.user.phone || "",
                company_name: order.user.company_name || "",
              }
            : null,

          // ✅ include product details
          product: {
            title: order.product?.title,
            material_color: order.material_color,
            quantity: order.quantity,
            paper_gsm: order.paper_gsm,
            orderStatus: order.status,
            description: order.product?.description || "",
            product_image: order.product?.product_image,
            size: order.size,
            print_coverage: order.print_coverage,
            lamination: order.lamination,
            burst_factor: order.burst_factor,
            no_of_ply: order.no_of_ply,
            created_at: order.created_at,
            is_active: order.product?.is_active,
          },

          // ✅ include supplier name, quotations, and nested supplier details
          suppliers:
            order.assignedOrders && order.assignedOrders.length > 0
              ? order.assignedOrders.map((ao) => ({
                  supplier_name: ao.supplier_name || "",
                  supplier_status: ao.status || "",
                  quotation_price: ao.quotation_price || "",
                  supplier_to_admin_quotation_price:
                    ao.supplier_to_admin_quotation_price || "",
                  supplier: ao.supplier
                    ? {
                        // needed to open a chat with this exact supplier
                        user_id: ao.supplier.user_id,
                        name: ao.supplier.name || "",
                        email: ao.supplier.email || "",
                        phone: ao.supplier.phone || "",
                        company_name: ao.supplier.company_name || "",
                        website: ao.supplier.website || "",
                        business_type: ao.supplier.business_type || "",
                      }
                    : null,
                }))
              : [],
        }));

        setData(grouped);
        setTotalPage(res?.data?.data?.pagination?.totalPages || 0);
      })
      .catch((err) => {
        if (err?.response?.data?.message === "Unauthorized!") Navigate("/");
      });
  };

  useEffect(() => {
    // Debounced so typing a search term doesn't fire a request per
    // keystroke — matters once this table has real volume behind it.
    const timer = setTimeout(() => {
      RequestForQuotationData();
    }, 350);
    return () => clearTimeout(timer);
  }, [page, query, statusFilter, monthFilter]);

  const handlePageClick = (e) => setPage(e.selected + 1);

  const formatDate = (d) =>
    d && !isNaN(new Date(d)) ? new Date(d).toLocaleString("en-IN") : "";

  const exportToExcel = () => {
    if (!data || data.length === 0) {
      toast.error("No data to export");
      return;
    }

    const exportData = data.flatMap((order) =>
      (order.suppliers && order.suppliers.length > 0
        ? order.suppliers
        : [{ supplier_name: "No supplier assigned" }]
      ).map((s) => ({
        "Order ID": order.order_id || "",
        "Product Title": order.product?.title || "",
        Quantity: order.quantity || "",
        Size:
          typeof order.size === "string"
            ? order.size.replace(/['"]+/g, "")
            : order.size || "",
        Material: order.material_color || "",
        "Order Status": order.order_status || "",
        "Buyer Name": order.user?.name || "",
        "Buyer Email": order.user?.email || "",
        "Buyer Phone": order.user?.phone || "",
        "Supplier Name": s.supplier_name || s.supplier?.name || "",
        "Supplier Company": s.supplier?.company_name || "",
        "Supplier Email": s.supplier?.email || "",
        "Supplier Phone": s.supplier?.phone || "",
        "1st Quotation": s.quotation_price || "",
        "2nd Quotation": s.supplier_to_admin_quotation_price || "",
        "Supplier Status": s.supplier_status || s.status || "",
        "Final Price (Admin)": order.final_price || "",
        "Final Description": order.final_description || "",
        "Product Description": order.product_description || "",
        "Created At": formatDate(order.created_at),
        "Updated At": formatDate(order.updated_at),
      }))
    );

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "RFQ Export");

    // Auto-fit columns width
    worksheet["!cols"] = Object.keys(exportData[0]).map((h) => ({
      wch: Math.min(Math.max(h.length + 5, 20), 50),
    }));

    // ================================
    // 🎨 Styling Section
    // ================================
    const range = XLSX.utils.decode_range(worksheet["!ref"]);
    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "1B55E2" } },
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "CCCCCC" } },
        bottom: { style: "thin", color: { rgb: "CCCCCC" } },
        left: { style: "thin", color: { rgb: "CCCCCC" } },
        right: { style: "thin", color: { rgb: "CCCCCC" } },
      },
    };
    const evenRow = {
      fill: { fgColor: { rgb: "F8F9FA" } },
      border: {
        top: { style: "thin", color: { rgb: "EEEEEE" } },
        bottom: { style: "thin", color: { rgb: "EEEEEE" } },
        left: { style: "thin", color: { rgb: "EEEEEE" } },
        right: { style: "thin", color: { rgb: "EEEEEE" } },
      },
    };
    const oddRow = {
      fill: { fgColor: { rgb: "FFFFFF" } },
      border: {
        top: { style: "thin", color: { rgb: "EEEEEE" } },
        bottom: { style: "thin", color: { rgb: "EEEEEE" } },
        left: { style: "thin", color: { rgb: "EEEEEE" } },
        right: { style: "thin", color: { rgb: "EEEEEE" } },
      },
    };

    // Apply header + zebra striping
    for (let R = range.s.r; R <= range.e.r; R++) {
      for (let C = range.s.c; C <= range.e.c; C++) {
        const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
        if (!worksheet[cellRef]) continue;

        if (R === 0) {
          worksheet[cellRef].s = headerStyle; // Header row
        } else {
          worksheet[cellRef].s = R % 2 === 0 ? evenRow : oddRow;
        }
      }
    }

    // ================================
    // 🧾 Save File
    // ================================
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
      cellStyles: true,
    });

    saveAs(
      new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
      }),
      `RFQ_Export_${new Date().toISOString().split("T")[0]}.xlsx`
    );

    toast.success("Exported successfully!");
  };

  const supStatusTint = (st = "") => {
    const s = String(st).toLowerCase();
    if (s === "approved" || s === "accepted")
      return { bg: "var(--color-success-bg)", color: "var(--color-success-text)", dot: "var(--color-success-text)" };
    if (s === "rejected")
      return { bg: "var(--color-danger-bg)", color: "var(--color-danger-text)", dot: "var(--color-danger-text)" };
    if (s === "negotiating" || s === "negotiation")
      return { bg: "var(--color-warning-bg)", color: "var(--color-warning-text)", dot: "var(--color-warning-text)" };
    return { bg: "var(--color-info-bg)", color: "var(--color-info-text)", dot: "var(--color-info-text)" };
  };

  const TINTS = [
    { bg: "var(--color-info-bg)", color: "var(--color-info-text)" },
    { bg: "var(--color-success-bg)", color: "var(--color-success-text)" },
    { bg: "var(--color-violet-bg)", color: "var(--color-violet-text)" },
    { bg: "var(--color-warning-bg)", color: "var(--color-warning-text)" },
  ];
  const tint = (s = "") => TINTS[(String(s).length) % TINTS.length];
  const initialsOf = (name = "") =>
    name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
  const money = (v) => `₹${Number(v || 0).toLocaleString("en-IN")}`;
  const effOf = (s) => Number(s?.supplier_to_admin_quotation_price || s?.quotation_price || 0);

  const SUP_GRID = { gridTemplateColumns: "1.4fr 1fr 1.15fr 1.05fr .8fr 130px" };

  const SupplierActions = ({ order, supplier }) => (
    <div className="al-actions">
      <button type="button" className="al-act" title="View quotes"
        onClick={() => Navigate("/admin/rfq-product-show", { state: { item: order.product } })}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
      </button>
      <button type="button" className="al-act" title="Message supplier" style={{ color: "#25D366" }}
        disabled={!supplier?.user_id}
        onClick={() =>
          openAdminChat({
            user_id: supplier?.user_id,
            name: supplier?.name,
            email: supplier?.email,
            user_type: "supplier",
          })
        }>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7A8.38 8.38 0 014 11.5a8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"/></svg>
      </button>
    </div>
  );

  const QuotesBtn = ({ order }) => (
    <button type="button" title="Revert / manage quotes"
      onClick={() =>
        Navigate(`/admin/revert-back/supplier/${order.product_id}/${order?.order_id}`, {
          state: {
            order_id: order.order_id,
            product_id: order.product_id,
            title: order.product_title,
            size: order.size,
            quantity: order.quantity,
          },
        })
      }
      style={{ display: "inline-flex", alignItems: "center", gap: 6, height: 28, padding: "0 10px", borderRadius: 8, border: "1px solid var(--color-border)", background: "var(--color-surface)", color: "var(--color-primary)", fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2h9l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"/><polyline points="14 2 14 8 20 8"/></svg>
      Quotes
    </button>
  );

  const StatusPill = ({ st }) => {
    const t = supStatusTint(st);
    return (
      <span className="al-pill" style={{ background: t.bg, color: t.color, textTransform: "capitalize", display: "inline-flex", alignItems: "center", gap: 5 }}>
        <span style={{ width: 5, height: 5, borderRadius: "50%", background: t.dot }} />
        {st}
      </span>
    );
  };

  const OrderStatusBadge = ({ status }) => {
    const t = supStatusTint(status);
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, color: t.color, background: t.bg, borderRadius: 8, padding: "5px 11px", textTransform: "capitalize" }}>
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: t.dot }} />
        {status || "—"}
      </span>
    );
  };

  const OrderCard = ({ order }) => {
    const suppliers = order.suppliers || [];
    const effs = suppliers.map(effOf).filter((n) => n > 0);
    const best = effs.length ? Math.min(...effs) : 0;
    return (
      <div className="al-card rfq-card" style={{ marginBottom: 16, overflow: "hidden" }}>
        <div className="rfq-ohead">
          <span className="rfq-oid">#{order.order_id}</span>
          <div style={{ minWidth: 0 }}>
            <div className="al-name">{order.product_title || "—"}</div>
            <div className="al-muted" style={{ fontSize: 11.5 }}>
              {(order.user?.name || "—")} · Qty {Number(order.quantity || 0).toLocaleString("en-IN")}
            </div>
          </div>
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span className="rfq-final-pill">
              Admin Final <strong style={{ fontFamily: "var(--font-mono)", color: order.final_price ? "var(--color-primary)" : "var(--color-text-tertiary)" }}>{order.final_price ? money(order.final_price) : "—"}</strong>
            </span>
            <OrderStatusBadge status={order.order_status} />
          </div>
        </div>

        {suppliers.length > 0 ? (
          <div className="al-tablewrap">
            <div style={{ minWidth: 760 }}>
              <div className="al-thead" style={SUP_GRID}>
                <span className="al-th">Supplier Name</span>
                <span className="al-th">1st Quotation</span>
                <span className="al-th">2nd Quotation</span>
                <span className="al-th">Status</span>
                <span className="al-th">Supplier Quotes</span>
                <span className="al-th al-th--right">Actions</span>
              </div>
              {suppliers.map((s, i) => {
                const at = tint(s.supplier_name);
                const q1 = Number(s.quotation_price || 0);
                const q2 = Number(s.supplier_to_admin_quotation_price || 0);
                const saving = q1 > 0 && q2 > 0 && q1 > q2 ? q1 - q2 : 0;
                const isBest = best > 0 && effOf(s) === best;
                return (
                  <div className="al-row" style={{ ...SUP_GRID, background: isBest ? "linear-gradient(90deg, var(--color-success-bg), transparent)" : undefined }} key={i}>
                    <div className="al-id">
                      <span className="al-avatar" style={{ background: at.bg, color: at.color }}>{initialsOf(s.supplier_name)}</span>
                      <div style={{ minWidth: 0 }}>
                        <div className="al-name">{s.supplier_name || "—"}</div>
                        {isBest && <div style={{ fontSize: 10, fontWeight: 700, color: "var(--color-success-text)" }}>★ Best price</div>}
                      </div>
                    </div>
                    <span className="al-mono al-muted" style={{ textDecoration: q1 > 0 && q2 > 0 ? "line-through" : "none" }}>
                      {q1 > 0 ? money(q1) : "—"}
                    </span>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
                      <span className="al-mono" style={{ fontWeight: 800 }}>{q2 > 0 ? money(q2) : "—"}</span>
                      {saving > 0 && (
                        <span style={{ fontSize: 10, fontWeight: 700, color: "var(--color-success-text)", background: "var(--color-success-bg)", borderRadius: 5, padding: "1px 5px", whiteSpace: "nowrap" }}>▼ {money(saving)}</span>
                      )}
                    </span>
                    <span>{s.supplier_status ? <StatusPill st={s.supplier_status} /> : <span className="al-muted">—</span>}</span>
                    <span><QuotesBtn order={order} /></span>
                    <SupplierActions order={order} supplier={s.supplier} />
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div style={{ padding: "34px 18px", textAlign: "center" }}>
            <div style={{ width: 44, height: 44, margin: "0 auto 12px", borderRadius: 12, background: "var(--color-surface-2)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--color-text-tertiary)" }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/></svg>
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--color-text-secondary)" }}>No supplier assigned</div>
          </div>
        )}

        <div className="rfq-ofoot">
          Order Status:&nbsp;<strong style={{ textTransform: "capitalize" }}>{order.order_status}</strong>
        </div>
      </div>
    );
  };

  const MobileOrderCard = ({ order }) => {
    const suppliers = order.suppliers || [];
    const effs = suppliers.map(effOf).filter((n) => n > 0);
    const best = effs.length ? Math.min(...effs) : 0;
    return (
      <div className="al-mcard">
        <div className="al-mcard__top">
          <span className="rfq-oid">#{order.order_id}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="al-name">{order.product_title}</div>
            <div className="al-muted" style={{ fontSize: 12 }}>{order.user?.name || "—"} · Qty {order.quantity}</div>
          </div>
          <OrderStatusBadge status={order.order_status} />
        </div>
        <div className="al-mrow">
          <span className="al-muted">Admin Final</span>
          <strong style={{ color: "var(--color-primary)", fontFamily: "var(--font-mono)" }}>{order.final_price ? money(order.final_price) : "—"}</strong>
        </div>
        {suppliers.length > 0 ? (
          suppliers.map((s, i) => {
            const q1 = Number(s.quotation_price || 0);
            const q2 = Number(s.supplier_to_admin_quotation_price || 0);
            const isBest = best > 0 && effOf(s) === best;
            return (
              <div key={i} style={{ border: `1px solid ${isBest ? "var(--color-success-text)" : "var(--color-border)"}`, borderRadius: 10, padding: 10, marginTop: 10, background: isBest ? "var(--color-success-bg)" : "var(--color-surface-2)" }}>
                <div className="al-name" style={{ marginBottom: 4 }}>{s.supplier_name} {isBest && <span style={{ fontSize: 10, color: "var(--color-success-text)" }}>★ Best</span>}</div>
                <div className="al-mrow"><span className="al-muted">1st Quote</span><strong style={{ textDecoration: q1 > 0 && q2 > 0 ? "line-through" : "none" }}>{q1 > 0 ? money(q1) : "—"}</strong></div>
                <div className="al-mrow"><span className="al-muted">2nd Quote</span><strong>{q2 > 0 ? money(q2) : "—"}</strong></div>
                <div className="al-mrow">
                  <span className="al-muted">Status</span>
                  {s.supplier_status ? <StatusPill st={s.supplier_status} /> : <span className="al-muted">—</span>}
                </div>
                <div className="al-mactions" style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                  <QuotesBtn order={order} />
                  <SupplierActions order={order} supplier={s.supplier} />
                </div>
              </div>
            );
          })
        ) : (
          <p className="al-muted" style={{ margin: "8px 0 0" }}>No supplier assigned</p>
        )}
      </div>
    );
  };

  // Search/status are now applied server-side (see RequestForQuotationData)
  // so they cover the whole dataset, not just whatever rows happen to be on
  // the current page. Only sorting stays client-side.
  let viewData = [...(data || [])];
  viewData.sort((a, b) =>
    sortNewest
      ? new Date(b.created_at) - new Date(a.created_at)
      : new Date(a.created_at) - new Date(b.created_at)
  );

  return (
    <div className="al">
      <Toaster position="top-center" reverseOrder={false} />

      <div className="al-head">
        <div>
          <h1 className="al-title">Request For Quotation</h1>
          <p className="al-sub">Compare supplier quotes per RFQ and pick the winning bid.</p>
        </div>
        <div className="al-head__actions">
          <button type="button" className="al-btn al-btn--primary" onClick={exportToExcel}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Export
          </button>
        </div>
      </div>

      <div className="al-card" style={{ padding: "13px 16px", marginBottom: 16 }}>
        <div className="al-toolbar" style={{ border: 0, padding: 0 }}>
          <div className="al-search">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-tertiary)" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/></svg>
            <input type="search" placeholder="Search by order, product, buyer, supplier…"
              value={query} onChange={(e) => { setQuery(e.target.value); setPage(1); }} />
          </div>
          <div className="al-select">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
            <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
              <option value="">Order Status</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
          <div className="al-select">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            <select value={monthFilter} onChange={(e) => { setMonthFilter(e.target.value); setPage(1); }}>
              <option value="">Select Month</option>
              <option value="1">Last 1 Month</option>
              <option value="3">Last 3 Months</option>
              <option value="6">Last 6 Months</option>
            </select>
          </div>
          <button type="button" className="al-tool" style={{ marginLeft: "auto" }} onClick={() => setSortNewest((v) => !v)} title="Toggle sort order">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M7 12h10M11 18h2"/></svg>
            Sort: {sortNewest ? "Newest" : "Oldest"}
          </button>
        </div>
      </div>

      {viewData.length > 0 ? (
        isMobile ? (
          <div className="al-mobile">
            {viewData.map((order, idx) => <MobileOrderCard order={order} key={idx} />)}
          </div>
        ) : (
          viewData.map((order, idx) => <OrderCard order={order} key={idx} />)
        )
      ) : (
        <div className="al-card">
          <div className="al-empty">
            <div className="al-empty__icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
            </div>
            <p className="al-empty__title">No Orders Found</p>
            <p className="al-empty__text">Quotation requests will appear here once orders are placed.</p>
          </div>
        </div>
      )}

      {data.length > 0 && (
        <div className="al-foot" style={{ borderTop: 0, paddingTop: 0 }}>
          <span className="al-foot__info">Showing <strong>{viewData.length}</strong> of {data.length} orders</span>
          <ReactPaginate
            previousLabel={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>}
            nextLabel={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>}
            breakLabel={"..."}
            pageCount={totalpage}
            marginPagesDisplayed={2}
            pageRangeDisplayed={2}
            onPageChange={handlePageClick}
            containerClassName={"pagination justify-content-end m-0"}
            pageClassName={"page-item"} pageLinkClassName={"page-link"}
            previousClassName={"page-item"} previousLinkClassName={"page-link"}
            nextClassName={"page-item"} nextLinkClassName={"page-link"}
            breakClassName={"page-item"} breakLinkClassName={"page-link"}
            activeClassName={"active"} />
        </div>
      )}

      {/* "Message supplier" (SupplierActions, above) now opens the real Live
          Chat widget from Layout/chatModal.jsx via openAdminChat() instead of
          this non-functional stub — its Send button had no onClick at all. */}
    </div>
  );
};

export default RequestForQuotation;
